import {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import {
  createBottomSheetStackStore,
  getSheetLayer,
  isTopSheet,
  sheetZIndex,
  type BottomSheetStackStore,
} from "./bottomSheetStackLogic";

export {
  BOTTOM_SHEET_BASE_Z,
  BOTTOM_SHEET_LAYER_STEP,
  createBottomSheetStackStore,
  getSheetLayer,
  isTopSheet,
  sheetZIndex,
} from "./bottomSheetStackLogic";

const EMPTY_OPEN_IDS: readonly string[] = [];

function noopSubscribe() {
  return () => {};
}

const BottomSheetStackContext = createContext<BottomSheetStackStore | null>(null);

export function BottomSheetStackProvider({ children }: { children: ReactNode }) {
  const storeRef = useRef<BottomSheetStackStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = createBottomSheetStackStore();
  }
  const store = storeRef.current;

  const openIds = useSyncExternalStore(store.subscribe, store.getOpenIds, store.getOpenIds);
  const hasOpenSheets = openIds.length > 0;

  useEffect(() => {
    if (!hasOpenSheets) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [hasOpenSheets]);

  return <BottomSheetStackContext.Provider value={store}>{children}</BottomSheetStackContext.Provider>;
}

export function useBottomSheetStack(open: boolean, onClose: () => void) {
  const store = useContext(BottomSheetStackContext);
  const reactId = useId();
  const sheetId = `bs-${reactId}`;

  useLayoutEffect(() => {
    if (!store || !open) return;
    store.register(sheetId, true);
    return () => store.register(sheetId, false);
  }, [store, sheetId, open]);

  const openIds = useSyncExternalStore(
    open && store ? store.subscribe : noopSubscribe,
    store?.getOpenIds ?? (() => EMPTY_OPEN_IDS),
    store?.getOpenIds ?? (() => EMPTY_OPEN_IDS),
  );

  const layer = open ? Math.max(getSheetLayer(openIds, sheetId), 1) : 0;
  const isTop = open && (store ? isTopSheet(openIds, sheetId) : true);
  const zIndex = sheetZIndex(layer);

  useEffect(() => {
    if (!open || !isTop) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, isTop, onClose]);

  return { zIndex, isTop };
}
